import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminOrdersPage.css';
import AdminNavbar from "../../Admin/Navbar/Navbar1";
import { makeAuthenticatedGETRequest, makeAuthenticatedPATCHRequest } from '../../services/serverHelper';
import { orderPoints } from '../../services/apis';
import AdminSidebar from '../Navbar/AdminSidebar';


const AdminOrdersPage = () => {
  // State management
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState({});

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [verificationStatusFilter, setVerificationStatusFilter] = useState('');
  const accessToken = localStorage.getItem('accessToken')
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  /**
   * Fetch orders from backend API
   */
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await makeAuthenticatedGETRequest(
        orderPoints.FETCH_ADMIN_ORDERS_API,
        accessToken
      )
      console.log(response.data);
      setOrders(response.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(
        err.response?.data?.message ||
        'Failed to fetch orders. Please try again later.'
      );
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /**
   * Apply filters and search to orders
   */
  useEffect(() => {
    let filtered = [...orders];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(order => {
        const orderId = order._id?.toString()?.toLowerCase() || '';
        const customerName = `${order.userId?.firstName || ''} ${order.userId?.lastName || ''}`
          .trim()
          .toLowerCase();
        const customerEmail = (order.emailId || order.userId?.email || '').toLowerCase();
        const transactionId = (
          order.payment?.transactionId ||
          order.transactionId ||
          order.utrNumber ||
          ''
        ).toLowerCase();

        return (
          orderId.includes(term) ||
          customerName.includes(term) ||
          customerEmail.includes(term) ||
          transactionId.includes(term)
        );
      });
    }

    // Payment status filter
    if (paymentStatusFilter) {
      const selectedStatus = paymentStatusFilter.toLowerCase();
      filtered = filtered.filter(order =>
        order.payment?.paymentStatus?.toLowerCase() === selectedStatus
      );
    }

    // Verification status filter
    if (verificationStatusFilter) {
      const isVerified = verificationStatusFilter === 'verified';
      filtered = filtered.filter(
        order => Boolean(order.access?.granted) === isVerified
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [
    orders,
    searchTerm,
    paymentStatusFilter,
    verificationStatusFilter,
  ]);

  /**
   * Handle payment verification
   */
  const handleVerifyPayment = async (orderId, status) => {
    try {
      setVerifying(prev => ({ ...prev, [orderId]: true }));

      const response = await makeAuthenticatedPATCHRequest(
        `${orderPoints.UPDATE_ORDER_STATUS_API}/${orderId}`,
        {
          paymentStatus:status
        },
        accessToken
      );

      // Update the order in state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId
            ? { ...order, verifiedPayment: true, ...response.data }
            : order
        )
      );
    } catch (err) {
      console.error('Error verifying payment:', err);
      alert(
        err.response?.data?.message ||
        'Failed to verify payment. Please try again.'
      );
    } finally {
      setVerifying(prev => ({ ...prev, [orderId]: false }));
    }
  };

  /**
   * Format date to readable format
   */
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  /**
   * Get status badge class
   */
  const getStatusBadgeClass = (status) => {
    const baseClass = 'status-badge-admin';
    switch (status) {
      case 'verified':
        return `${baseClass} badge-paid`;
      case 'pending':
        return `${baseClass} badge-pending`;
      case 'failed':
        return `${baseClass} badge-failed`;
      default:
        return baseClass;
    }
    

    return baseClass;
  };

  /**
   * Get verification badge
   */
  const getVerificationBadge = (isVerified) => {
    return isVerified
      ? 'status-badge-admin badge-verified'
      : 'status-badge-admin badge-unverified';
  };

  /**
   * Get fallback image
   */
  const getProductImage = (imageUrl) => {
    return imageUrl || 'https://via.placeholder.com/50?text=No+Image';
  };

  /**
   * Pagination calculations
   */
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Render loading state
  if (loading) {
    return (
      <div className="admin-orders-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <>
        <AdminNavbar />
        <div className="dashboard-layout">
          <AdminSidebar />
          <div className="admin-orders-page">
          {/* Header */}
          <div className="page-header">
              <h1>Orders Management</h1>
              <p className="subtitle">Manage all marketplace orders and verify payments</p>
          </div>

          {/* Error Banner */}
          {error && (
              <div className="error-banner">
              <span>{error}</span>
              <button className="close-btn" onClick={() => setError(null)}>
                  ×
              </button>
              </div>
          )}

          {/* Search and Filter Section */}
          <div className="search-filter-section">
              <div className="filter-group search-group">
                <div className="filter-group-title">Search</div>
                <div className="search-container">
                  <input
                      type="text"
                      placeholder="Search by Order ID, Customer Name, or Transaction ID..."
                      className="search-input"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                  />
                  <span className="search-icon">🔍</span>
                </div>
              </div>

              <div className="filter-group status-group">
                <div className="filter-group-title">Order Filters</div>
                <div className="filter-container">
                  <select
                      className="filter-select"
                      value={paymentStatusFilter}
                      onChange={e => setPaymentStatusFilter(e.target.value)}
                  >
                      <option value="">All Payment Status</option>
                      <option value="Verified">Verified</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                  </select>

                  <select
                      className="filter-select"
                      value={verificationStatusFilter}
                      onChange={e => setVerificationStatusFilter(e.target.value)}
                  >
                      <option value="">All Verification Status</option>
                      <option value="verified">Verified</option>
                      <option value="unverified">Unverified</option>
                  </select>

                  {(searchTerm ||
                      paymentStatusFilter ||
                      verificationStatusFilter) && (
                      <button
                        className="reset-btn"
                        onClick={() => {
                            setSearchTerm('');
                            setPaymentStatusFilter('');
                            setVerificationStatusFilter('');
                        }}
                      >
                        Reset Filters
                      </button>
                  )}
                </div>
              </div>
          </div>

          {/* Results Count */}
          <div className="results-info">
              <p>
              Showing <strong>{paginatedOrders.length}</strong> of{' '}
              <strong>{filteredOrders.length}</strong> orders
              </p>
          </div>

          {/* Empty State */}
          {filteredOrders.length === 0 ? (
              <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h2>No Orders Found</h2>
              <p>
                  {searchTerm || paymentStatusFilter || verificationStatusFilter
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No marketplace orders available yet.'}
              </p>
              </div>
          ) : (
              <>
              {/* Table Container */}
              <div className="table-container">
                  <table className="orders-table">
                  <thead>
                      <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      <th>Order Status</th>
                      <th>Verified</th>
                      <th>Transaction ID</th>
                      <th>Date</th>
                      <th>Action</th>
                      </tr>
                  </thead>
                  <tbody>
                      {paginatedOrders.map((order, index) => (
                      <tr key={order._id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                          {/* Order ID */}
                          <td className="order-id">
                          <span className="id-badge">{order._id?.slice(-8)}</span>
                          </td>

                          {/* Customer */}
                          <td>
                          <div className="customer-info">
                              <div className="customer-name">{`${order.userId.firstName} ${order.userId.lastName}` || '-'}</div>
                              <div className="customer-email">
                              {order.emailId || '-'}
                              </div>
                          </div>
                          </td>

                          {/* Product */}
                          <td>
                          <div className="product-info">
                              <div className="product-image">
                              <img
                                  src={getProductImage(order.productImage)}
                                  alt={order.productName || 'Product'}
                                  onError={e => (e.target.src = 'https://via.placeholder.com/50?text=No+Image')}
                              />
                              </div>
                              <div className="product-details">
                              <div className="product-name" title={order.productName}>
                                  {order.productName || '-'}
                              </div>
                              <div className="seller-name">
                                  by {order.sellerName || 'Unknown'}
                              </div>
                              <div className="product-quantity">
                                  Qty: {order.quantity || 1}
                              </div>
                              </div>
                          </div>
                          </td>

                          {/* Amount */}
                          <td className="amount">
                          <strong>{formatCurrency(order.payment.amount)}</strong>
                          </td>

                          {/* Payment Status */}
                          <td>
                          <span className={getStatusBadgeClass(order.payment.paymentStatus)}>
                              {order.payment.paymentStatus || 'pending'}
                          </span>
                          <div className="payment-method">
                              {order.payment.mode || '-'}
                          </div>
                          </td>

                          {/* Order Status */}
                          <td>
                          <span className={getStatusBadgeClass(order.payment.paymentStatus)}>
                              {order.payment.paymentStatus || 'pending'}
                          </span>
                          </td>

                          {/* Verified Status */}
                          <td>
                          <span className={getVerificationBadge(order.verifiedPayment)}>
                              {order.access.granted ? 'Verified' : 'Pending'}
                          </span>
                          </td>

                          {/* Transaction ID */}
                          <td className="transaction-id">
                          <span style={{wordBreak:"break-word"}} title={order.transactionId || order.utrNumber || '-'}>
                              {(order.payment.transactionId || order.utrNumber || '-')}
                              {(order.payment.transactionId || order.utrNumber || '')}
                          </span>
                          </td>

                          {/* Date */}
                          <td className="date">
                          <div className="date-created">
                              {formatDate(order.createdAt)}
                          </div>
                          </td>

                          {/* Action */}
                          <td className="action-cell">
                          { order.payment.paymentStatus  === "Verified" ? (
                              <button className="verify-btn" style={{backgroundColor:"green", color:"white"}} disabled>
                              ✓ Verified
                              </button>
                          ) : order.payment.paymentStatus  === "failed" ? (
                              <button className="verify-btn" style={{backgroundColor:"red", color:"white"}} disabled>
                              X Failed
                              </button>
                          ) : (
                            <div style={{display: "flex", gap:"10px" }}>
                              <button
                                className="verify-btn verify-btn-active"
                                onClick={() => handleVerifyPayment(order._id, "Verified")}
                                disabled={verifying[order._id]}
                              >
                              {verifying[order._id] ? 'Verifying...' : 'Verify Payment'}
                              </button>
                              <button
                                className="verify-btn verify-btn-active"
                                onClick={() => handleVerifyPayment(order._id, "failed")}
                                disabled={verifying[order._id]}
                                style={{backgroundColor:"red", color:"white"}}
                              >
                              {/* {verifying[order._id] ? 'Verifying...' : 'Verify Payment'} */}
                              Failed
                              </button>
                            </div>
                          ) 
                          }
                          </td>
                      </tr>
                      ))}
                  </tbody>
                  </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                  <div className="pagination-container">
                  <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                  >
                      ← Previous
                  </button>

                  <div className="pagination-info">
                      Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </div>

                  <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                  >
                      Next →
                  </button>
                  </div>
              )}
              </>
          )}

          {/* Refresh Button */}
          <button className="refresh-btn" onClick={fetchOrders} title="Refresh orders">
              🔄 Refresh
          </button>
          </div>
        </div>
    </>
  );
};

export default AdminOrdersPage;
